import { z } from 'zod';
import { hasAlarmDeviceNamePrefix, removeAlarmMeasuredDetailText, resolveCommandDeliveryDisplay, resolveEntryAlarmDisplay } from '@/domains/alarms/alarm-display-text';
import { mapAlarmMessageToAlarmItemLevel, type AlarmItemLevel } from '@/domains/alarms/alarm-event-display';
import { isAlarmChannelMessageType, WsEventType, type WsBaseMessage } from '@/shared/realtime/ws-types';

// ─── 타입 ────────────────────────────────────────────────────────────────────

export type { AlarmItemLevel };

export const alarmItemSchema = z.object({
  id: z.string(),
  level: z.enum(['info', 'caution', 'warning', 'measure']),
  /** ISO 8601 */
  occurredAt: z.string(),
  locationName: z.string(),
  description: z.string(),
  /** 썸네일 이미지 URL. null이면 이미지 없음. */
  imageUrl: z.string().nullable().optional(),
  fieldUid: z.string().optional(),
  deviceName: z.string().optional(),
});

export const alarmListSnapshotSchema = z.object({
  items: z.array(alarmItemSchema),
  /** 'sse': 실제 백엔드 데이터 / 'mock': 목업 데이터 */
  source: z.enum(['sse', 'mock']),
  updatedAt: z.string(),
  /** ALARM_COUNT_CHANGED를 수신할 때마다 증가하는 REST 정합성 복구 revision */
  reconciliationRevision: z.number().int().nonnegative(),
});

export type AlarmItem = z.infer<typeof alarmItemSchema>;
export type AlarmListSnapshot = z.infer<typeof alarmListSnapshotSchema>;
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const UUID_TEST_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{12}\b/i;
// ─── 목업 데이터 ───────────────────────────────────────────────────────────────

/**
 * 이미지 스크린샷 레이아웃 확인용 그레이 플레이스홀더 (64×48 SVG)
 */
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2264%22%20height%3D%2248%22%3E%3Crect%20width%3D%2264%22%20height%3D%2248%22%20fill%3D%22%2394a3b8%22%2F%3E%3C%2Fsvg%3E';

const MOCK_OCCURRED_AT = '2026-12-31T23:59:00+09:00';
const MOCK_LOCATION = '파주운정3차 아파트';
const MOCK_DESCRIPTION = '지하2층 우수조 외부 지역 온도 주의 발생 (CO: 0ppm, O2: 20.8%, H2S: 0ppm, Exp: 0%LEL, 온도: 17.8°C, 습도: 58%)';

export const MOCK_ALARM_ITEMS: AlarmItem[] = [
  {
    id: 'mock-1',
    level: 'caution',
    occurredAt: MOCK_OCCURRED_AT,
    locationName: MOCK_LOCATION,
    description: MOCK_DESCRIPTION,
    imageUrl: PLACEHOLDER_IMAGE,
  },
  {
    id: 'mock-2',
    level: 'warning',
    occurredAt: MOCK_OCCURRED_AT,
    locationName: MOCK_LOCATION,
    description: MOCK_DESCRIPTION,
  },
  {
    id: 'mock-3',
    level: 'caution',
    occurredAt: MOCK_OCCURRED_AT,
    locationName: MOCK_LOCATION,
    description: MOCK_DESCRIPTION,
  },
  {
    id: 'mock-4',
    level: 'warning',
    occurredAt: MOCK_OCCURRED_AT,
    locationName: MOCK_LOCATION,
    description: MOCK_DESCRIPTION,
    imageUrl: PLACEHOLDER_IMAGE,
  },
  {
    id: 'mock-5',
    level: 'caution',
    occurredAt: MOCK_OCCURRED_AT,
    locationName: MOCK_LOCATION,
    description: MOCK_DESCRIPTION,
  },
];

// ─── 스냅샷 팩토리 ─────────────────────────────────────────────────────────────

export function createEmptyAlarmListSnapshot(): AlarmListSnapshot {
  return { items: [], source: 'sse', updatedAt: new Date().toISOString(), reconciliationRevision: 0 };
}

export function createMockAlarmListSnapshot(): AlarmListSnapshot {
  return { items: MOCK_ALARM_ITEMS, source: 'mock', updatedAt: new Date().toISOString(), reconciliationRevision: 0 };
}

// ─── 메시지 변환 ───────────────────────────────────────────────────────────────

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * ALARM payload에서 화면에 표시할 장비명을 추출합니다.
 */
function resolveAlarmDeviceDisplayName(...sources: Array<Record<string, unknown> | undefined>): string | null {
  for (const source of sources) {
    if (!source) continue;

    const candidate = readString(source.device_name ?? source.deviceName ?? source.name);
    if (candidate && !UUID_TEST_PATTERN.test(candidate)) {
      return candidate;
    }
  }

  return null;
}

/**
 * 안전지키미 이동 이벤트의 간단 표시명을 추출합니다.
 */
function resolveMovementDisplayLabel(...sources: Array<Record<string, unknown> | undefined>): string | null {
  for (const source of sources) {
    if (!source) continue;
    const candidate = readString(source.movement_display_label);
    if (candidate) {
      return candidate;
    }
  }
  return null;
}

/**
 * 표시 문자열 안의 UID 토큰을 장비명으로 치환합니다.
 */
function sanitizeAlarmDisplayText(value: unknown, deviceName: string | null): string | null {
  const text = readString(value);
  if (!text) return null;

  return removeAlarmMeasuredDetailText(text.replace(UUID_PATTERN, deviceName || '장비'));
}

/**
 * 알람 원문을 "장비명-이벤트" 형태로 축약합니다.
 */
function summarizeAlarmMessage(source: string | null | undefined, deviceName: string | null): string | null {
  const text = readString(source);
  if (!text) return null;

  const normalizedDeviceName = deviceName ? deviceName.replace(UUID_PATTERN, '장비') : null;
  let summary = text
    .replace(UUID_PATTERN, normalizedDeviceName || '장비')
    .replace(/\s*데이터\s*수신\s*/g, ' ')
    .replace(/\s*알람이\s*발생했습니다\.?\s*/g, ' ')
    .replace(/\s*알람\s*발생\s*/g, ' ')
    .replace(/\s*즉시\s*확인\s*바랍니다\.?\s*/g, ' ')
    .replace(/\s*이벤트가\s*수신되었습니다\.?\s*/g, ' ')
    .replace(/\s*에서\s*/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalizedDeviceName && !hasAlarmDeviceNamePrefix(summary, normalizedDeviceName)) {
    summary = `${normalizedDeviceName}-${summary}`;
  }

  summary = summary
    .replace(/\s*(경고|주의|알림|알람)$/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .trim();

  return summary || null;
}

/**
 * 위험알리미 S+ legacy 알람은 백엔드에서 내려준 제목과 내용을 그대로 표시합니다.
 */
function shouldKeepExplicitAlarmTitle(title: string | null): boolean {
  return (
    title === '위험알리미 S+ 센서 데이터 수신' ||
    title === '장비 명령을 전송하지 못했습니다.' ||
    title === '정보 확인' ||
    / - 정보 확인$/.test(title ?? '') ||
    /발생$/.test(title ?? '')
  );
}

/**
 * 새 알람 계약의 결과형 제목만 장비명 접두어를 붙입니다.
 */
function shouldPrefixExplicitAlarmTitle(title: string | null): boolean {
  return title === '정보 확인' || /발생$/.test(title ?? '');
}

/**
 * 간결한 알람 제목은 장비명과 결합해 상단 표시 문구로 사용합니다.
 */
function summarizeExplicitAlarmTitle(title: string | null, deviceName: string | null): string | null {
  if (!title) return null;
  if (!deviceName || title.startsWith(`${deviceName}-`) || title.startsWith(`${deviceName} - `)) return title;
  return `${deviceName} - ${title}`;
}

export function resolveAlarmMessageFieldUid(message: WsBaseMessage, body: Record<string, unknown>): string | undefined {
  return (
    readString((message as WsBaseMessage & { field_uid?: unknown }).field_uid) ??
    readString((message as WsBaseMessage & { fieldUid?: unknown }).fieldUid) ??
    readString(body.field_uid) ??
    readString(body.fieldUid) ??
    undefined
  );
}

/** 백엔드 ALARM 메시지 event에서 알림 목록 표시 등급을 결정합니다. */
function resolveAlarmItemLevel(message: WsBaseMessage): AlarmItemLevel {
  return mapAlarmMessageToAlarmItemLevel(message.event, message.type);
}

/**
 * 백엔드 알람성 WsBaseMessage를 AlarmItem으로 변환합니다.
 *
 * 예상 body 필드:
 * ```
 * {
 *   alarm_uid      : string           // 알림 고유 ID
 *   title          : string           // 알림 제목
 *   content        : string           // 알림 본문
 *   location_name  : string           // 현장/구역명
 *   field_name     : string           // 대체 현장명
 *   description    : string           // 알림 설명
 *   image_url      : string | null    // 썸네일 이미지 URL (선택)
 *   field_uid      : string           // 현장 UID (필터링용, 선택)
 * }
 * ```
 */
export function deriveAlarmItemFromMessage(message: WsBaseMessage): AlarmItem | null {
  const isImmediateVisualAlarm =
    message.event === WsEventType.SACON_PRODUCT_ALERT || message.event === WsEventType.SACON_PRODUCT_WARNING || message.event === WsEventType.DEVICE_GEOFENCE_ESCAPE;
  if (!isAlarmChannelMessageType(message.type) && !isImmediateVisualAlarm) return null;

  const body = (message.body ?? {}) as Record<string, unknown>;

  const id =
    readString(body.alarm_uid) ??
    readString(body.event_uid) ??
    readString(body.eventUid) ??
    readString(body.source_trace_id) ??
    readString(body.sourceTraceId) ??
    readString(body.uid) ??
    readString(message.key) ??
    null;

  if (!id) return null;

  const meta = body.meta as Record<string, unknown> | undefined;
  const detail = meta?.detail as Record<string, unknown> | undefined;
  const detailBody = detail?.body as Record<string, unknown> | undefined;
  const deviceName = resolveAlarmDeviceDisplayName(body, meta, detail, detailBody);
  const entryDisplay = resolveEntryAlarmDisplay({
    provider: body.provider ?? meta?.provider,
    event: body.alarm_code ?? message.event,
    title: sanitizeAlarmDisplayText(body.title, deviceName),
    content: sanitizeAlarmDisplayText(body.content, deviceName),
    summaryContent: sanitizeAlarmDisplayText(body.summary_content, deviceName),
    deviceName,
    archiveReadOnly: meta?.archive_read_only,
  });
  const movementDisplayLabel = resolveMovementDisplayLabel(body, meta, detail, detailBody);
  const movementSummary = movementDisplayLabel ? (deviceName ? `${deviceName}-${movementDisplayLabel}` : movementDisplayLabel) : null;
  const level = resolveAlarmItemLevel(message);
  const rawDisplayMessage =
    movementSummary ??
    sanitizeAlarmDisplayText(body.description, deviceName) ??
    sanitizeAlarmDisplayText(body.message, deviceName) ??
    sanitizeAlarmDisplayText(body.alarm_message, deviceName) ??
    sanitizeAlarmDisplayText(body.content, deviceName) ??
    sanitizeAlarmDisplayText(body.title, deviceName);
  const commandDeliveryDisplay = resolveCommandDeliveryDisplay(rawDisplayMessage, deviceName);
  const explicitTitle = sanitizeAlarmDisplayText(body.title, deviceName);
  const keepExplicitTitle = shouldKeepExplicitAlarmTitle(explicitTitle) && rawDisplayMessage !== null;
  const explicitSummary = keepExplicitTitle ? (shouldPrefixExplicitAlarmTitle(explicitTitle) ? summarizeExplicitAlarmTitle(explicitTitle, deviceName) : explicitTitle) : null;
  const summary = summarizeAlarmMessage(rawDisplayMessage, deviceName);
  const locationName =
    entryDisplay?.title ??
    movementSummary ??
    commandDeliveryDisplay?.title ??
    explicitSummary ??
    summary ??
    sanitizeAlarmDisplayText(body.location_name, deviceName) ??
    sanitizeAlarmDisplayText(body.field_name, deviceName) ??
    sanitizeAlarmDisplayText(body.zone_name, deviceName) ??
    sanitizeAlarmDisplayText(body.title, deviceName) ??
    deviceName ??
    '알 수 없는 위치';

  const description = entryDisplay?.content ?? movementSummary ?? commandDeliveryDisplay?.description ?? rawDisplayMessage ?? '';

  const imageUrl = readString(body.image_url ?? body.imageUrl) ?? null;
  const fieldUid = resolveAlarmMessageFieldUid(message, body);

  return {
    id,
    level,
    occurredAt: readString(message.sent_at) ?? new Date().toISOString(),
    locationName,
    description,
    imageUrl: imageUrl ?? undefined,
    fieldUid,
    deviceName: deviceName ?? undefined,
  };
}

/**
 * 새 ALARM 메시지를 스냅샷에 반영합니다. (최신순 정렬, 최대 50개)
 */
export function applyAlarmMessage(snapshot: AlarmListSnapshot, message: WsBaseMessage): AlarmListSnapshot {
  const item = deriveAlarmItemFromMessage(message);
  if (!item) return snapshot;

  const items = [item, ...snapshot.items.filter(i => i.id !== item.id)].slice(0, 50);

  return { ...snapshot, source: 'sse', items, updatedAt: new Date().toISOString() };
}

/**
 * 알람 count 변경 신호를 REST 목록 정합성 복구 revision으로 반영합니다.
 */
export function applyAlarmListReconciliationSignal(snapshot: AlarmListSnapshot): AlarmListSnapshot {
  return {
    ...snapshot,
    reconciliationRevision: snapshot.reconciliationRevision + 1,
    updatedAt: new Date().toISOString(),
  };
}
